import { useEffect, useRef } from 'react';
import { config } from '../config';
import { authService } from '../services/authService';
import type { TrafoAlertEvent, VoltageAlertEvent, CableAlertEvent, AlertEvent } from './useMonitoringAlerts';

/**
 * Persistent SSE hook connecting to /api/alerts/stream (zone-independent).
 * Unlike useMonitoringAlerts, the dependency array is [] — the connection
 * persists across zone switches so cross-zone alerts are never missed.
 */
export function useGlobalAlertStream(
  onAlert: (alert: AlertEvent) => void,
): void {
  const onAlertRef = useRef(onAlert);
  useEffect(() => {
    onAlertRef.current = onAlert;
  });

  useEffect(() => {
    let eventSource: EventSource | null = null;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 10;
    let reconnectDelay = 1000;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    function connect() {
      if (disposed) return;

      const token = authService.getToken();
      const baseUrl = `${config.apiBaseUrl}/api/alerts/stream`;
      const url = token ? `${baseUrl}?access_token=${encodeURIComponent(token)}` : baseUrl;

      eventSource = new EventSource(url);

      eventSource.addEventListener('trafo_alert', (event) => {
        try {
          const data: TrafoAlertEvent = JSON.parse(event.data);
          onAlertRef.current({ type: 'trafo_alert', data });
        } catch (err) {
          console.error('[GlobalAlertStream] Failed to parse trafo_alert:', err);
        }
      });

      eventSource.addEventListener('voltage_alert', (event) => {
        try {
          const data: VoltageAlertEvent = JSON.parse(event.data);
          onAlertRef.current({ type: 'voltage_alert', data });
        } catch (err) {
          console.error('[GlobalAlertStream] Failed to parse voltage_alert:', err);
        }
      });

      eventSource.addEventListener('cable_alert', (event) => {
        try {
          const data: CableAlertEvent = JSON.parse(event.data);
          onAlertRef.current({ type: 'cable_alert', data });
        } catch (err) {
          console.error('[GlobalAlertStream] Failed to parse cable_alert:', err);
        }
      });

      eventSource.addEventListener('connected', () => {
        reconnectAttempts = 0;
        reconnectDelay = 1000;
      });

      eventSource.onerror = () => {
        if (disposed) return;
        eventSource?.close();
        eventSource = null;

        if (reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;
          reconnectTimer = setTimeout(connect, reconnectDelay);
          reconnectDelay = Math.min(reconnectDelay * 2, 30000);
        } else {
          console.error('[GlobalAlertStream] Max reconnection attempts reached');
        }
      };
    }

    connect();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      eventSource?.close();
      eventSource = null;
    };
  }, []);
}
